/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CountryDto } from '../models/CountryDto';
import type { SiteDto } from '../models/SiteDto';
import type { SiteGeolocationDto } from '../models/SiteGeolocationDto';
import type { SiteGeolocationInput } from '../models/SiteGeolocationInput';
import type { SiteListDto } from '../models/SiteListDto';
import type { SiteMetaCityDto } from '../models/SiteMetaCityDto';
import type { SiteSearchResult } from '../models/SiteSearchResult';
import type { SiteUpdateDto } from '../models/SiteUpdateDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class SiteService {

    /**
     * Get sites list for relocation
     * @param studyId
     * @param studyLanguage
     * @param city
     * @param patientZipCode
     * @param take
     * @param skip
     * @returns SiteSearchResult
     * @throws ApiError
     */
    public static siteControllerRelocation(
        studyId?: string,
        studyLanguage?: string,
        city?: string,
        patientZipCode?: string,
        take?: number,
        skip?: number,
    ): CancelablePromise<SiteSearchResult> {
        return __request({
            method: 'GET',
            path: `/site/relocation`,
            query: {
                'studyId': studyId,
                'studyLanguage': studyLanguage,
                'city': city,
                'patientZipCode': patientZipCode,
                'take': take,
                'skip': skip,
            },
        });
    }

    /**
     * Get sites by name and department
     * @param siteName
     * @param department
     * @returns SiteDto
     * @throws ApiError
     */
    public static siteControllerGetSiteByNameAndDepartment(
        siteName?: string,
        department?: string,
    ): CancelablePromise<SiteDto> {
        return __request({
            method: 'GET',
            path: `/site/getDuplicated`,
            query: {
                'siteName': siteName,
                'department': department,
            },
        });
    }

    /**
     * Get sites
     * @param includeSubsites
     * @param studyId
     * @param studyLanguage
     * @param city
     * @param siteType
     * @param countryCode
     * @param siteCode
     * @param siteStatus
     * @param zipCode
     * @param siteName
     * @param siteAddress
     * @param department
     * @param isRelocation
     * @param skip
     * @param take
     * @returns SiteDto
     * @throws ApiError
     */
    public static siteControllerList(
        includeSubsites?: boolean,
        studyId?: Array<string>,
        studyLanguage?: string,
        city?: string,
        siteType?: Array<'UNIVERSITY' | 'HOSPITAL' | 'VIRTUAL' | 'OTHER' | 'SMO' | 'PROFESSIONAL_RESEARCH_SITE' | 'DOCTORS_PRACTICE'>,
        countryCode?: Array<string>,
        siteCode?: string,
        siteStatus?: Array<'SETUP' | 'CANCELED' | 'ACTIVE' | 'INACTIVE' | 'PAUSED' | 'CLOSEOUT' | 'COMPLETED' | 'REMOVED'>,
        zipCode?: string,
        siteName?: string,
        siteAddress?: string,
        department?: string,
        isRelocation: boolean = false,
        skip?: number,
        take?: number,
    ): CancelablePromise<Array<SiteDto>> {
        return __request({
            method: 'GET',
            path: `/site`,
            query: {
                'includeSubsites': includeSubsites,
                'studyId': studyId,
                'studyLanguage': studyLanguage,
                'city': city,
                'siteType': siteType,
                'countryCode': countryCode,
                'siteCode': siteCode,
                'siteStatus': siteStatus,
                'zipCode': zipCode,
                'siteName': siteName,
                'siteAddress': siteAddress,
                'department': department,
                'isRelocation': isRelocation,
                'skip': skip,
                'take': take,
            },
        });
    }

    /**
     * Create site
     * @param requestBody
     * @returns SiteDto
     * @throws ApiError
     */
    public static siteControllerCreate(
        requestBody: SiteUpdateDto,
    ): CancelablePromise<SiteDto> {
        return __request({
            method: 'POST',
            path: `/site`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get sites
     * @param includeSubsites
     * @param studyId
     * @param studyLanguage
     * @param city
     * @param siteType
     * @param countryCode
     * @param siteCode
     * @param siteStatus
     * @param zipCode
     * @param siteName
     * @param siteAddress
     * @param department
     * @param isRelocation
     * @param skip
     * @param take
     * @returns SiteSearchResult
     * @throws ApiError
     */
    public static siteControllerListPreview(
        includeSubsites?: boolean,
        studyId?: Array<string>,
        studyLanguage?: string,
        city?: string,
        siteType?: Array<'UNIVERSITY' | 'HOSPITAL' | 'VIRTUAL' | 'OTHER' | 'SMO' | 'PROFESSIONAL_RESEARCH_SITE' | 'DOCTORS_PRACTICE'>,
        countryCode?: Array<string>,
        siteCode?: string,
        siteStatus?: Array<'SETUP' | 'CANCELED' | 'ACTIVE' | 'INACTIVE' | 'PAUSED' | 'CLOSEOUT' | 'COMPLETED' | 'REMOVED'>,
        zipCode?: string,
        siteName?: string,
        siteAddress?: string,
        department?: string,
        isRelocation: boolean = false,
        skip?: number,
        take?: number,
    ): CancelablePromise<SiteSearchResult> {
        return __request({
            method: 'GET',
            path: `/site/listPreview`,
            query: {
                'includeSubsites': includeSubsites,
                'studyId': studyId,
                'studyLanguage': studyLanguage,
                'city': city,
                'siteType': siteType,
                'countryCode': countryCode,
                'siteCode': siteCode,
                'siteStatus': siteStatus,
                'zipCode': zipCode,
                'siteName': siteName,
                'siteAddress': siteAddress,
                'department': department,
                'isRelocation': isRelocation,
                'skip': skip,
                'take': take,
            },
        });
    }

    /**
     * Get countries having sites
     * @returns CountryDto
     * @throws ApiError
     */
    public static siteControllerListCountries(): CancelablePromise<Array<CountryDto>> {
        return __request({
            method: 'GET',
            path: `/site/countries`,
        });
    }

    /**
     * Get site
     * @param id
     * @param includesubsites
     * @returns SiteDto
     * @throws ApiError
     */
    public static siteControllerGet(
        id: string,
        includesubsites?: boolean,
    ): CancelablePromise<SiteDto> {
        return __request({
            method: 'GET',
            path: `/site/${id}`,
            query: {
                'includesubsites': includesubsites,
            },
        });
    }

    /**
     * Update site
     * @param id
     * @param requestBody
     * @returns SiteDto
     * @throws ApiError
     */
    public static siteControllerUpdate(
        id: string,
        requestBody: SiteUpdateDto,
    ): CancelablePromise<SiteDto> {
        return __request({
            method: 'PUT',
            path: `/site/${id}`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Delete site
     * @param id
     * @returns void
     * @throws ApiError
     */
    public static siteControllerDelete(
        id: string,
    ): CancelablePromise<void> {
        return __request({
            method: 'DELETE',
            path: `/site/${id}`,
            errors: {
                412: `Error that should be passed to user.`,
            },
        });
    }

    /**
     * Get Site Geolocation
     * @param requestBody
     * @returns SiteGeolocationDto
     * @throws ApiError
     */
    public static siteControllerGeolocation(
        requestBody: SiteGeolocationInput,
    ): CancelablePromise<SiteGeolocationDto> {
        return __request({
            method: 'POST',
            path: `/site/geolocation`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get Sites for a specific investigator
     * @param id
     * @returns SiteListDto
     * @throws ApiError
     */
    public static siteControllerByInvestigator(
        id: string,
    ): CancelablePromise<Array<SiteListDto>> {
        return __request({
            method: 'GET',
            path: `/site/byInvestigator/${id}`,
        });
    }

    /**
     * Get matching cities for Meta by providing a location name
     * @param location
     * @returns SiteMetaCityDto
     * @throws ApiError
     */
    public static siteControllerMetaCities(
        location: string,
    ): CancelablePromise<Array<SiteMetaCityDto>> {
        return __request({
            method: 'GET',
            path: `/site/metaCities/${location}`,
        });
    }

}